import React, { useState, useEffect, useCallback, useRef } from "react";
import { Upload, Trash, TagSimple, Buildings, Cube, Wrench, Hammer, Calendar, Folder } from "@phosphor-icons/react";
import { getStatusColor } from "../../../shared/components/uploadFileModal/uploadFileModal";
import { FileLogStatus } from "shared/types/file";
import { ResourceType } from "../../../shared/types/resource";
import { FileThumbnail } from "shared/components/upload/FileThumbnail";

interface PicturePreviewProps {
  file: File;
  index: number;
  isSelected: boolean;
  isBulkActive: boolean;
  isUploading?: boolean;
  isRemoving?: boolean;
  fileLog: any;
  resources: any[];
  onToggleSelection: (index: number, selected: boolean) => void;
  onUploadFile: (index: number) => void;
  onRemoveFile: (index: number) => void;
  onDescriptionChange: (index: number, description: string) => void;
  onDateChange?: (index: number, date: string) => void;
  canUpload?: (fileLog: any, index: number) => boolean;
}

const PicturePreview: React.FC<PicturePreviewProps> = ({
  file,
  index,
  isSelected,
  isBulkActive,
  isUploading = false,
  isRemoving = false,
  fileLog,
  resources,
  onToggleSelection,
  onUploadFile,
  onRemoveFile,
  onDescriptionChange,
  onDateChange,
  canUpload,
}) => {
  const [localDescription, setLocalDescription] = useState(fileLog?.fileLog?.summary || "");
  const debounceRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const currentDescription = fileLog?.fileLog?.summary || "";
    if (currentDescription !== localDescription) {
      setLocalDescription(currentDescription);
    }
  }, [fileLog?.fileLog?.summary]);

  const handleDescriptionChange = useCallback((value: string) => {
    setLocalDescription(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onDescriptionChange(index, value);
    }, 500);
  }, [index, onDescriptionChange]);

  const handlePickerEvent = (mode: string) => {
    const event = new CustomEvent('file-upload-open-picker', {
      detail: {
        mode,
        fileIndex: index,
        bulkSelection: isBulkActive
      }
    });
    window.dispatchEvent(event);
  };

  const canUploadFile = canUpload ? canUpload(fileLog, index) : true;

  return (
    /* `animate-pulse` used to be applied to this whole card while uploading.
       Tailwind's pulse animates opacity from 1 to .5 and back on a loop, so every
       uploading card - and with concurrent uploads that is several at once, each
       out of phase - visibly dimmed and brightened. A steady tint and border
       communicate "in progress" without the card breathing. */
    <div className={`relative group transition-all duration-300 ease-in-out ${
      isRemoving ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'
    } ${
      isUploading ? 'bg-moss-primary/10 ring-1 ring-moss-primary/40' : 'bg-gray-50'
    } ${
      isSelected ? 'ring-2 ring-moss-primary bg-moss-primary/5 rounded-lg' : ''
    } p-3 rounded-lg`}>
      {/* Upload progress overlay. A single spinner is the only thing that moves:
          it reads as activity, where a bouncing icon over a pulsing card read as
          the interface glitching. */}
      {isUploading && (
        <div className="absolute inset-0 bg-moss-primary/15 rounded-lg flex items-center justify-center z-20">
          <div className="bg-white rounded-full px-3 py-2 shadow-md flex items-center gap-2">
            <div
              className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-moss-primary/25 border-t-moss-primary"
              aria-hidden
            />
            <span className="text-xs font-medium text-neutral-primary">
              Uploaden...
            </span>
          </div>
        </div>
      )}

      {/* Removal overlay */}
      {isRemoving && (
        <div className="absolute inset-0 bg-red-500/20 rounded-lg flex items-center justify-center z-20">
          <div className="bg-white rounded-full p-2 shadow-md">
            <div className="h-4 w-4 rounded-full bg-green-500 flex items-center justify-center">
              <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Selection checkbox */}
      <div className="absolute top-2 left-2 z-10">
        <input
          type="checkbox"
          className="w-4 h-4 rounded-sm border border-neutral-primary/30 cursor-pointer"
          style={{ accentColor: '#5B7304' }}
          checked={isSelected}
          onChange={(e) => onToggleSelection(index, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      <div className="relative overflow-hidden rounded-lg mb-3">
        {/* A downscaled thumbnail, not the original: decoding a 12MP photo to fill
            a 160px tile cost ~48MB of bitmap per card. */}
        <FileThumbnail file={file} className="h-40 w-full object-cover" />

        {/* Action buttons */}
        <div className="absolute top-2 right-2 flex gap-1">
          <button
            onClick={() => !isUploading && canUploadFile && onUploadFile(index)}
            disabled={isUploading || !canUploadFile}
            className={`rounded-full p-1.5 transition-all ${
              isUploading
                ? 'bg-gray-400 text-gray-300 cursor-not-allowed opacity-50'
                : !canUploadFile
                ? 'bg-gray-400 text-gray-300 cursor-not-allowed opacity-50'
                : 'bg-moss-primary text-white opacity-0 group-hover:opacity-100 hover:bg-moss-primary/80'
            }`}
            title={
              isUploading
                ? "Uploading..."
                : !canUploadFile
                ? "Vul eerst label en gebouw/unit in"
                : "Upload file"
            }
          >
            <Upload size={12} />
          </button>
          <button
            onClick={() => onRemoveFile(index)}
            className="bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
            title="Remove file"
          >
            <Trash size={12} />
          </button>
        </div>

        {/* Status indicator */}
        {fileLog?.fileLog?.status && fileLog?.fileLog?.status !== FileLogStatus.UNKNOWN && (
          <div className="absolute top-2 right-20">
            <div className={`w-3 h-3 rounded-full ${getStatusColor(fileLog.fileLog.status)}`}></div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {/* File name */}
        <p className="text-xs text-gray-600 truncate font-medium">{file.name}</p>

        {/* Label - Always visible */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePickerEvent('label');
          }}
          className="w-full text-left px-2 py-1.5 rounded-sm border text-xs hover:bg-neutral-primary/5 border-neutral-primary/20 bg-white text-neutral-primary transition-all duration-200 ease-in-out hover:shadow-sm hover:border-moss-primary/40 focus:ring-2 focus:ring-moss-primary/20 focus:outline-hidden"
          title={isBulkActive && isSelected ? `Set label for selected pictures` : `Set label for this picture`}
        >
          <div className="flex items-center gap-2">
            <TagSimple className="h-3 w-3 text-moss-primary shrink-0" />
            <span className="truncate text-moss-primary font-medium">
              {fileLog?.fileLog?.fileLabel?.label || 'Label'}
            </span>
          </div>
        </button>

        {/* Quick description */}
        <input
          type="text"
          placeholder="Voeg beschrijving toe..."
          value={localDescription}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          className="w-full text-xs bg-white border border-gray-200 rounded-sm px-2 py-1.5 placeholder-gray-400 text-gray-700 focus:border-moss-primary/50 focus:ring-1 focus:ring-moss-primary/20 outline-hidden"
          onClick={(e) => e.stopPropagation()}
        />

        {/* Document Date Input */}
        <div className="relative">
          {isBulkActive && isSelected ? (
            // Bulk selection mode - show button to trigger bulk date picker
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePickerEvent('documentDate');
              }}
              className="w-full text-left px-2 py-1.5 rounded-sm border text-xs hover:bg-neutral-primary/5 border-neutral-primary/20 bg-white text-neutral-primary transition-all duration-200 ease-in-out hover:shadow-sm hover:border-moss-primary/40 focus:ring-2 focus:ring-moss-primary/20 focus:outline-hidden"
              title={`Stel datum in voor geselecteerde foto's`}
            >
              <div className="flex items-center gap-2">
                <Calendar className="h-3 w-3 text-neutral-secondary-mid shrink-0" />
                <span className="text-neutral-primary">
                  {fileLog?.fileLog?.documentDate || 'Datum'}
                </span>
              </div>
            </button>
          ) : (
            // Single selection mode - show date input
            <>
              <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                <Calendar className="h-3 w-3 text-neutral-secondary-mid" />
              </div>
              <input
                type="date"
                value={fileLog?.fileLog?.documentDate || ""}
                onChange={(e) => onDateChange && onDateChange(index, e.target.value)}
                className="w-full text-xs bg-white border border-gray-200 rounded-sm pl-7 pr-2 py-1.5 text-gray-700 focus:border-moss-primary/50 focus:ring-1 focus:ring-moss-primary/20 outline-hidden"
                onClick={(e) => e.stopPropagation()}
                title="Foto datum"
              />
            </>
          )}
        </div>

        {/* Building and Unit Picker Buttons - stacked so the unit ID is always fully visible */}
        <div className="flex flex-col gap-1">
          {/* Building Picker Button */}
          {(() => {
            const buildingId = fileLog?.fileLog?.buildingId;
            const buildingSelected = !!buildingId;
            const buildingLabel = buildingId
              ? (resources.find(r => r.id === buildingId && r.type === ResourceType.BUILDING)?.resourceId || buildingId)
              : 'Gebouw';
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePickerEvent('building');
                }}
                className={`w-full text-left px-1.5 py-1.5 h-full rounded-sm border text-[11px] transition-all duration-200 ease-in-out hover:shadow-sm focus:ring-2 focus:ring-moss-primary/20 focus:outline-hidden min-w-0 ${
                  buildingSelected
                    ? 'bg-moss-primary/10 border-moss-primary text-moss-primary hover:bg-moss-primary/15'
                    : 'bg-white border-neutral-primary/20 text-neutral-primary hover:bg-neutral-primary/5 hover:border-moss-primary/40'
                }`}
                title={
                  buildingSelected
                    ? `Gebouw: ${buildingLabel} (klik om te wijzigen of te verwijderen)`
                    : (isBulkActive && isSelected ? `Set building for selected pictures` : `Set building for this picture`)
                }
              >
                <div className="flex items-center gap-1 min-w-0">
                  <Buildings className={`h-3 w-3 shrink-0 ${buildingSelected ? 'text-moss-primary' : 'text-neutral-secondary-mid'}`} />
                  <span className="truncate">{buildingLabel}</span>
                </div>
              </button>
            );
          })()}

          {/* Unit Picker Button */}
          {(() => {
            const unitId = fileLog?.fileLog?.unitId;
            const unitSelected = !!unitId;
            const unitLabel = unitId
              ? (resources.find(r => r.id === unitId && r.type === ResourceType.UNIT)?.resourceId || unitId)
              : 'Unit';
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePickerEvent('unit');
                }}
                className={`w-full text-left px-1.5 py-1.5 h-full rounded-sm border text-[11px] transition-all duration-200 ease-in-out hover:shadow-sm focus:ring-2 focus:ring-moss-primary/20 focus:outline-hidden min-w-0 ${
                  unitSelected
                    ? 'bg-moss-primary/10 border-moss-primary text-moss-primary hover:bg-moss-primary/15'
                    : 'bg-white border-neutral-primary/20 text-neutral-primary hover:bg-neutral-primary/5 hover:border-moss-primary/40'
                }`}
                title={
                  unitSelected
                    ? `Unit: ${unitLabel} (klik om te wijzigen of te verwijderen)`
                    : (isBulkActive && isSelected ? `Set unit for selected pictures` : `Set unit for this picture`)
                }
              >
                <div className="flex items-center gap-1 min-w-0">
                  <Cube className={`h-3 w-3 shrink-0 ${unitSelected ? 'text-moss-primary' : 'text-neutral-secondary-mid'}`} />
                  <span className="truncate">{unitLabel}</span>
                </div>
              </button>
            );
          })()}
        </div>

        {/* Renovation Project and Part Picker Buttons */}
        <div className="flex gap-1">
          {/* Renovation Project Picker Button */}
          {(() => {
            const renovationSelected = !!fileLog?.fileLog?.customData?.renovationProjectTitle;
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePickerEvent('renovation-project');
                }}
                className={`flex-1 text-left px-1.5 py-1.5 h-full rounded-sm border text-[11px] transition-all duration-200 ease-in-out hover:shadow-sm focus:ring-2 focus:ring-moss-primary/20 focus:outline-hidden min-w-0 ${
                  renovationSelected
                    ? 'bg-moss-primary/10 border-moss-primary text-moss-primary hover:bg-moss-primary/15'
                    : 'bg-white border-neutral-primary/20 text-neutral-primary hover:bg-neutral-primary/5 hover:border-moss-primary/40'
                }`}
                title={isBulkActive && isSelected ? `Selecteer renovatieproject voor geselecteerde foto's` : `Selecteer renovatieproject voor deze foto`}
              >
                <div className="flex items-center gap-1 min-w-0">
                  <Wrench className={`h-3 w-3 shrink-0 ${renovationSelected ? 'text-moss-primary' : 'text-neutral-secondary-mid'}`} />
                  <span className="truncate">
                    {fileLog?.fileLog?.customData?.renovationProjectTitle || 'Renovatie'}
                  </span>
                </div>
              </button>
            );
          })()}

          {/* Project Part Picker Button */}
          {(() => {
            const projectPartSelected = !!fileLog?.fileLog?.customData?.projectPartTitle;
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePickerEvent('project-part');
                }}
                className={`flex-1 text-left px-1.5 py-1.5 h-full rounded-sm border text-[11px] transition-all duration-200 ease-in-out hover:shadow-sm focus:ring-2 focus:ring-moss-primary/20 focus:outline-hidden min-w-0 ${
                  projectPartSelected
                    ? 'bg-moss-primary/10 border-moss-primary text-moss-primary hover:bg-moss-primary/15'
                    : 'bg-white border-neutral-primary/20 text-neutral-primary hover:bg-neutral-primary/5 hover:border-moss-primary/40'
                }`}
                title={isBulkActive && isSelected ? `Selecteer projectonderdeel voor geselecteerde foto's` : `Selecteer projectonderdeel voor deze foto`}
              >
                <div className="flex items-center gap-1 min-w-0">
                  <Hammer className={`h-3 w-3 shrink-0 ${projectPartSelected ? 'text-moss-primary' : 'text-neutral-secondary-mid'}`} />
                  <span className="truncate">
                    {fileLog?.fileLog?.customData?.projectPartTitle || 'Onderdeel'}
                  </span>
                </div>
              </button>
            );
          })()}
        </div>

        {/* Secondary Labels - Always visible area with add button */}
        <div className="flex flex-wrap gap-1 min-h-[24px] items-center">
          {fileLog?.fileLog?.secondaryLabels && fileLog.fileLog.secondaryLabels.length > 0 ? (
            fileLog.fileLog.secondaryLabels.map((label: any, i: number) => {
              const labelText = typeof label === 'string' ? label : label?.label;
              if (!labelText) return null;
              return (
                <span
                  key={`${labelText}-${i}`}
                  className="bg-moss-primary/10 text-moss-primary px-1.5 py-0.5 rounded-sm text-xs font-medium inline-flex items-center gap-1"
                >
                  {labelText}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.dispatchEvent(
                        new CustomEvent('file-upload-remove-secondary-label', {
                          detail: { fileIndex: index, label: labelText },
                        }),
                      );
                    }}
                    className="hover:text-red-600 cursor-pointer leading-none"
                    title={`Verwijder ${labelText}`}
                    aria-label={`Verwijder ${labelText}`}
                  >
                    ×
                  </button>
                </span>
              );
            })
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePickerEvent('secondaryLabel');
              }}
              className="text-xs text-neutral-secondary-mid hover:text-moss-primary flex items-center gap-1 px-1"
              title="Secundair label toevoegen"
            >
              <TagSimple size={12} />
              Secundair label
            </button>
          )}
          {fileLog?.fileLog?.secondaryLabels && fileLog.fileLog.secondaryLabels.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePickerEvent('secondaryLabel');
              }}
              className="text-xs text-neutral-secondary-mid hover:text-moss-primary flex items-center"
              title="Secundair label toevoegen"
            >
              <TagSimple size={12} />
            </button>
          )}
        </div>

        {/* Acquisition File Picker Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePickerEvent('acquisition-file');
          }}
          className="w-full text-left px-1.5 py-1.5 rounded-sm border text-[11px] hover:bg-neutral-primary/5 border-neutral-primary/20 bg-white text-neutral-primary transition-all duration-200 ease-in-out hover:shadow-sm hover:border-moss-primary/40 focus:ring-2 focus:ring-moss-primary/20 focus:outline-hidden"
          title={isBulkActive && isSelected ? `Koppel acquisitiedossier aan geselecteerde foto's` : `Koppel acquisitiedossier aan deze foto`}
        >
          <div className="flex items-center gap-1.5">
            <Folder className="h-3 w-3 text-neutral-secondary-mid shrink-0" weight="duotone" />
            <span className="truncate">
              {fileLog?.fileLog?.customData?.acquisitionName || fileLog?.fileLog?.customData?.acquisitionId ? `#ACQ-${fileLog?.fileLog?.customData?.acquisitionId}` : 'Acquisitiedossier'}
            </span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default PicturePreview;